/**
 * First-touch marketing attribution.
 *
 * The signup POST itself knows nothing about where the visitor came from: its
 * referrer is our own app, and the campaign parameters fell off the URL several
 * navigations ago. So the landing visit records them, and signup replays them.
 *
 * FIRST touch wins, not last: the campaign that first brought someone here is
 * what earned the signup, even if they wandered off and returned directly a
 * week later. Overwriting on every visit would credit the last accidental
 * direct visit and make every campaign look worthless.
 *
 * Stored client-side, so treat it as a hint rather than a fact — the server
 * length-caps it and uses it for reporting only.
 */

const KEY = 'dc_attribution';

const UTM_PARAMS = [
  ['utm_source', 'utmSource'],
  ['utm_medium', 'utmMedium'],
  ['utm_campaign', 'utmCampaign'],
];

/**
 * Record how this visitor arrived, once per browser. Safe to call on every app
 * load: it returns early when something is already stored.
 *
 * Called for its side effect at startup, before any signup can happen.
 */
export const captureAttribution = () => {
  try {
    if (localStorage.getItem(KEY)) return;

    const params = new URLSearchParams(window.location.search);
    const data = {};
    for (const [param, field] of UTM_PARAMS) {
      const value = params.get(param);
      if (value) data[field] = value.slice(0, 60);
    }

    // Only an EXTERNAL referrer is worth recording — an internal one just says
    // they clicked a link on our own site.
    const ref = document.referrer;
    if (ref && !ref.startsWith(window.location.origin)) {
      data.referrer = ref.slice(0, 300);
    }

    // Which page they landed on. A signup from a blog post is a different
    // acquisition story than one from the pricing page.
    data.landingPath = window.location.pathname.slice(0, 120);

    localStorage.setItem(KEY, JSON.stringify(data));
  } catch {
    // Private mode, disabled storage, or an exotic URL — attribution is a
    // nice-to-have and must never break the page that renders the signup form.
  }
};

/** The stored attribution, or an empty object. Sent with signup requests. */
export const getAttribution = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY)) || {};
  } catch {
    return {};
  }
};

/*
 * Referral code (/?ref=CODE), kept apart from first-touch attribution: a
 * friend's link should win even for someone who visited before, so the LAST
 * code seen is kept, for 30 days. Replayed at signup as `referralCode`; the
 * server decides whether it is real.
 */
const REF_KEY = 'dc_ref';
const REF_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const REF_SHAPE = /^[A-Za-z0-9]{6,12}$/;

export const captureReferralCode = () => {
  try {
    const raw = new URLSearchParams(window.location.search).get('ref');
    if (!raw || !REF_SHAPE.test(raw)) return;
    localStorage.setItem(REF_KEY, JSON.stringify({ code: raw.toUpperCase(), at: Date.now() }));
  } catch {
    // Storage unavailable: the visitor can still sign up, just without the bonus.
  }
};

/** The stored referral code, or null when absent, expired or unreadable. */
export const getReferralCode = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(REF_KEY));
    if (!stored?.code || !stored.at || Date.now() - stored.at > REF_TTL_MS) return null;
    return stored.code;
  } catch {
    return null;
  }
};

/** Forget a code the server said is not real, so the banner stops promising it. */
export const clearReferralCode = () => {
  try { localStorage.removeItem(REF_KEY); } catch { /* nothing to clear */ }
};

/*
 * Coupon code (/?coupon=CODE): a creator's giveaway link. Kept for 30 days
 * like the referral code and applied the first time the visitor is signed in
 * with a verified email (CouponAutoApply in CouponRedeem.jsx). LAST code seen
 * wins: the link they just clicked is the one they mean.
 */
const COUPON_KEY = 'dc_coupon';
const COUPON_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** A code the way the server reads it: uppercase, letters and digits only, 4 to 20 long, or null. */
export const normalizeCouponCode = (raw) => {
  if (typeof raw !== 'string') return null;
  const code = raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  return code.length >= 4 && code.length <= 20 ? code : null;
};

export const captureCouponCode = () => {
  try {
    const code = normalizeCouponCode(new URLSearchParams(window.location.search).get('coupon'));
    if (!code) return;
    localStorage.setItem(COUPON_KEY, JSON.stringify({ code, at: Date.now() }));
  } catch {
    // Storage unavailable: the code can still be typed on the billing page.
  }
};

/** The stored coupon code, or null when absent, expired or unreadable. */
export const getCouponCode = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(COUPON_KEY));
    if (!stored?.code || !stored.at || Date.now() - stored.at > COUPON_TTL_MS) return null;
    return stored.code;
  } catch {
    return null;
  }
};

export const clearCouponCode = () => {
  try { localStorage.removeItem(COUPON_KEY); } catch { /* nothing to clear */ }
};
